import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Minimize2 } from 'lucide-react';
import './floating-panel.css';

const STORAGE_KEY_PREFIX = 'designer-fp-pos-';

/**
 * FloatingPanel — a draggable, position:fixed overlay for torn-off editor sections.
 *
 * Props:
 *   panelId   – stable string key (used for localStorage persistence)
 *   title     – short label shown in the title bar
 *   onDock    – callback to re-dock this panel back into the sidebar
 *   children  – panel body content
 *   initialX  – default left position (px)
 *   initialY  – default top position (px)
 */
const FloatingPanel = ({ panelId, title, onDock, children, initialX = 120, initialY = 120 }) => {
    // Restore persisted position or use defaults
    const [pos, setPos] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY_PREFIX + panelId);
            if (saved) return JSON.parse(saved);
        } catch (_) { }
        return { x: initialX, y: initialY };
    });

    const dragging = useRef(false);
    const offset = useRef({ dx: 0, dy: 0 });
    const panelRef = useRef(null);

    // Persist position on change
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_PREFIX + panelId, JSON.stringify(pos));
        } catch (_) { }
    }, [pos, panelId]);

    const onMouseDown = useCallback((e) => {
        // Only drag from the title bar (not from buttons inside it)
        if (e.target.closest('button')) return;
        e.preventDefault();
        dragging.current = true;
        offset.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    }, [pos]);

    useEffect(() => {
        const onMouseMove = (e) => {
            if (!dragging.current) return;
            const nx = Math.max(0, Math.min(e.clientX - offset.current.dx, window.innerWidth - 100));
            const ny = Math.max(0, Math.min(e.clientY - offset.current.dy, window.innerHeight - 40));
            setPos({ x: nx, y: ny });
        };
        const onMouseUp = () => { dragging.current = false; };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, []);

    return (
        <div
            ref={panelRef}
            className="floating-panel"
            style={{ left: pos.x, top: pos.y }}
        >
            <div className="floating-panel-bar" onMouseDown={onMouseDown}>
                <span className="floating-panel-title">{title}</span>
                <button
                    className="floating-panel-dock-btn"
                    onClick={onDock}
                    title="Dock back to sidebar"
                >
                    <Minimize2 size={14} />
                </button>
            </div>
            <div className="floating-panel-body">
                {children}
            </div>
        </div>
    );
};

export default FloatingPanel;
